import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import defaultTheme from '../Config/defaultTheme';
import { mockThemeSettings, commonFonts } from '../Config/mockThemeData';

/**
 * ThemeProvider Component
 * Applies dynamic CSS custom properties based on theme settings from Redux store
 */
const ThemeProvider = ({ themeSettings = null, children }) => {

    /**
     * Convert camelCase to kebab-case for CSS variable names
     */
    const toKebabCase = (str) => {
        return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    };

    /**
     * Apply CSS custom properties to document root
     */
    const applyThemeVariables = useCallback((theme) => {
        if (!theme) return;

        const root = document.documentElement;
        const fontFallback =
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

        // Apply color variables
        if (theme.colors) {
            Object.entries(theme.colors).forEach(([key, value]) => {
                root.style.setProperty(`--color-${toKebabCase(key)}`, value);
            });
        }

        // Apply font variables
        if (theme.font) {
            Object.entries(theme.font).forEach(([key, value]) => {
                const cssValue = typeof value === 'number' ? `${value}px` : value;
                root.style.setProperty(`--font-${toKebabCase(key)}`, cssValue);
            });
        }

        // Apply common fonts
        if (commonFonts) {
            root.style.setProperty('--font-family', `${commonFonts.PoppinsR}, ${fontFallback}`);
            root.style.setProperty('--font-family-heading', `${commonFonts.PoppinsSB}, ${fontFallback}`);
            root.style.setProperty('--font-family-light', commonFonts.PoppinsL);
            root.style.setProperty('--font-family-medium', commonFonts.PoppinsM);
            root.style.setProperty('--font-family-bold', commonFonts.PoppinsB);
        }

        // Apply spacing variables
        if (theme.spacing) {
            Object.entries(theme.spacing).forEach(([key, value]) => {
                const cssValue = typeof value === 'number' ? `${value}px` : value;
                root.style.setProperty(`--spacing-${toKebabCase(key)}`, cssValue);
            });
        }

        // Apply layout variables
        if (theme.layout) {
            Object.entries(theme.layout).forEach(([key, value]) => {
                let cssValue = value;

                if (typeof value === 'number') {
                    cssValue = `${value}px`;
                } else if (typeof value === 'boolean') {
                    cssValue = value ? '1' : '0';
                }

                root.style.setProperty(`--layout-${toKebabCase(key)}`, cssValue);
            });
        }

        // Apply effect variables
        if (theme.effects) {
            Object.entries(theme.effects).forEach(([key, value]) => {
                root.style.setProperty(`--effect-${toKebabCase(key)}`, value);
            });
        }
    }, []);

    /**
     * Apply theme when themeSettings change
     */
    useEffect(() => {
        if (themeSettings) {
            applyThemeVariables(themeSettings);
        } else {
            const isThemeDemoPage = window.location.pathname.includes('theme-demo');
            applyThemeVariables(isThemeDemoPage ? mockThemeSettings : defaultTheme);
        }
    }, [themeSettings, applyThemeVariables]);

    return <>{children}</>;
};

const mapStateToProps = (globalState) => ({
    themeSettings: globalState.mainReducerData.themeSettings
});

export default connect(mapStateToProps)(ThemeProvider);
